// Banka sayfası (hesaplar, alt hesaplar, kredi kartları) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'finance.bank';

export const bankGuide = (t: TFunction): GuideContent => ({
  layout: 'list',
  title: t(`${G}.title`, 'Banka ekranı nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Banka hesaplarınızı, alt hesaplarını ve bunlara bağlı kredi kartlarını tek listede görürsünüz. Para yatırma, para çekme ve transferler de bu ekrandan başlar. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    stats: t(`${G}.regions.stats`, 'Özet kartları'),
    filters: t(`${G}.regions.filters`, 'Hesap arama'),
    toolbar: t(`${G}.regions.toolbar`, 'Yeni Banka Hesabı ve ⋮'),
    table: t(`${G}.regions.table`, 'Hesap listesi'),
  },
  steps: [
    {
      region: 'stats',
      title: t(`${G}.s1.title`, '1. Özet kartlarını okuyun'),
      text: t(`${G}.s1.text`, 'Toplam Banka Bakiyesi, listede görünen ana banka hesaplarının bakiyelerinin toplamıdır. Yanındaki kartlar bugünkü giriş / çıkış sayısını ve tutarını, bekleyen tahsilatları ve bekleyen ödemeleri gösterir.'),
      tips: [
        t(`${G}.s1.tip1`, 'Alt hesap bakiyeleri Toplam Banka Bakiyesine eklenmez; her alt hesabın bakiyesi kendi satırında görünür.'),
        t(`${G}.s1.tip2`, 'Farklı para birimindeki hesaplar bu kartta ayrılmadan toplanır; dövizli hesaplarınız varsa satırlardaki bakiyelere bakın.'),
        t(`${G}.s1.tip3`, 'Arama yaptığınızda toplam yalnız bulunan hesaplardan hesaplanır.'),
        t(`${G}.s1.tip4`, 'Şube yöneticisi olarak girdiyseniz yalnız kendi şubenizin hesaplarını, toplam bakiyeyi ve hesap sayısını görürsünüz.'),
      ],
    },
    {
      region: 'filters',
      title: t(`${G}.s2.title`, '2. Hesabı bulun'),
      text: t(`${G}.s2.text`, '"Banka Hesabı Ara..." kutusuna banka adı, banka şubesi, hesap numarası veya IBAN yazın; liste anında daralır.'),
    },
    {
      region: 'toolbar',
      title: t(`${G}.s3.title`, '3. Hesap ekleyin, ⋮ menüsünü kullanın'),
      text: t(`${G}.s3.text`, '"Yeni Banka Hesabı" yeni bir ana banka hesabı açar. Yanındaki ⋮ düğmesinde ek işlemler bulunur.'),
      tips: [
        t(`${G}.s3.tip1`, 'Yenile: listeyi ve bakiyeleri yeniden yükler.'),
        t(`${G}.s3.tip2`, 'Kredi Kartları ve Krediler: ilgili sayfalara geçer.'),
        t(`${G}.s3.tip3`, 'Hareket Raporu: tüm banka hareketlerini listeleyen rapora gider.'),
        t(`${G}.s3.tip4`, 'Excel İndir: listedeki hesapları alt hesaplarıyla birlikte dosyaya aktarır.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s4.title`, '4. Listeyi okuyun'),
      text: t(`${G}.s4.text`, 'Her satır bir ana banka hesabıdır. Banka adının solundaki ok, o hesabın alt hesaplarını ve bağlı kredi kartlarını açar. Hesap Türü sütunu Standart, POS, Çek veya POS ve Çek Hesabı olarak görünür.'),
      tips: [
        t(`${G}.s4.tip1`, 'Bakiye her hesabın yalnız kendi bakiyesidir; ana hesabın bakiyesine alt hesaplar eklenmez. Yeşil artı, kırmızı eksi bakiyedir.'),
        t(`${G}.s4.tip2`, 'Kredi kartı satırlarında Bakiye yerine kartın borcu ve kalan limiti görünür.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s5.title`, '5. "İşlem Yap" ile para hareketi girin'),
      text: t(`${G}.s5.text`, 'Satırdaki "İşlem Yap" hesabın ayrıntılarını ve Hesap İşlemleri düğmelerini açar. Alt hesap satırında yapılan işlem yalnız o alt hesabın bakiyesini değiştirir.'),
      tips: [
        t(`${G}.s5.tip1`, 'Para Yatırma: tutar hesabın bakiyesine eklenir.'),
        t(`${G}.s5.tip2`, 'Para Çekme: tutar hesaptan düşülür; bakiyeden fazla tutar çekilemez.'),
        t(`${G}.s5.tip3`, 'Banka Transferi: başka bir banka hesabına aktarır. Kasa Transferi: bir kasaya aktarır.'),
        t(`${G}.s5.tip4`, 'Bu düğmeleri göremiyorsanız hesabınızda para işlemi veya transfer yetkisi yoktur.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s6.title`, '6. Satırın ⋮ menüsü'),
      text: t(`${G}.s6.text`, 'Satır sonundaki ⋮ düğmesi Düzenle, Banka Transferi, Kasa Transferi, Alt Hesap Ekle, Kredi Kartı Ekle ve Kredi Ekle seçeneklerini açar. Çek hesabı olarak işaretli hesaplarda Çek Defterleri de görünür.'),
      tips: [
        t(`${G}.s6.tip1`, 'Sil: işlem geçmişi olan hesap ve alt hesap silinemez. Alt hesabı olan ana hesabı silmek için önce alt hesapları silin. Silme geri alınamaz.'),
        t(`${G}.s6.tip2`, 'Kredi kartı satırının ⋮ menüsünde Hareketler, Harcama Ekle, Ödeme Yap, Düzenle ve Sil bulunur.'),
        t(`${G}.s6.tip3`, 'Alt Hesap Ekle, Kredi Ekle, Çek Defterleri ve Sil yalnız işletme sahibine görünür.'),
      ],
    },
    {
      region: 'pagination',
      title: t(`${G}.s7.title`, '7. Sayfalar arasında gezin'),
      text: t(`${G}.s7.text`, 'Liste uzunsa alttaki sayfalama ile ilerleyin ve sayfa başına kaç satır görüneceğini seçin. Başlıktaki (i) düğmesi bu rehberi her zaman yeniden açar.'),
    },
  ],
});
